/**
 * components/dashboard/RightPanel.tsx
 * Fixed: all array/object accesses guarded against undefined.
 */

import { useRef, useEffect, useState } from 'react';
import { Send, Bot, Newspaper } from 'lucide-react';
import clsx from 'clsx';
import { useUi, useTicker } from '../../store';
import { useTickerAnalysis }            from '../../hooks/useTickerAnalysis';
type RightPanelTab = 'ai' | 'news';

// ── AI Coach ──────────────────────────────────────────────────

function AICoachPanel() {
  const ticker  = useTicker();
  const { sendChatMessage } = useTickerAnalysis();
  const [input, setInput]   = useState('');
  const bottomRef           = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticker.aiMessages]);

  async function handleSend() {
    const text = input.trim();
    if (!text || ticker.aiLoading) return;
    setInput('');
    await sendChatMessage(text);
  }

  // Ensure aiMessages is always an array
  const messages = Array.isArray(ticker.aiMessages) ? ticker.aiMessages : [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 ? (
          <div className="text-2xs text-ink-3 leading-relaxed p-2">
            <Bot size={16} className="mb-2 text-bull opacity-60" />
            Select a ticker and I'll analyze the setup — catalyst strength,
            signal alignment, best strike, and exact entry timing.
            Or ask me anything about the trade.
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={clsx(
              'rounded-lg p-2.5 text-xs leading-relaxed',
              msg.role === 'user'
                ? 'bg-bg-2 text-ink-2'
                : 'bg-bull/5 border border-bull/10 text-ink-1'
            )}>
              <div className={clsx(
                'text-2xs font-mono mb-1',
                msg.role === 'user' ? 'text-ink-3' : 'text-bull'
              )}>
                {msg.role === 'user' ? 'You' : 'AI Coach'}
              </div>
              <div className="whitespace-pre-wrap">{msg.content}</div>
            </div>
          ))
        )}

        {ticker.aiLoading && (
          <div className="bg-bull/5 border border-bull/10 rounded-lg p-2.5">
            <div className="text-2xs text-bull font-mono mb-1">AI Coach</div>
            <div className="flex gap-1">
              {[0, 0.15, 0.3].map((d, i) => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-bull/40 animate-pulse"
                     style={{ animationDelay: `${d}s` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="flex gap-2 p-3 border-t border-border">
        <input
          className="input text-xs py-1.5 flex-1"
          placeholder="Ask about the setup…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          disabled={ticker.aiLoading}
        />
        <button
          onClick={handleSend}
          disabled={ticker.aiLoading || !input.trim()}
          className="btn-primary px-2.5 py-1.5 disabled:opacity-40"
        >
          <Send size={12} />
        </button>
      </div>
    </div>
  );
}

// ── News Panel ────────────────────────────────────────────────

function NewsPanel() {
  const { news, sym, isLoading: tickerLoading } = useTicker();
  const ticker = { isLoading: tickerLoading };

  // Guard: ensure news is always an array
  const safeNews = Array.isArray(news) ? news : [];

  const catConfig: Record<string, { label: string; cls: string }> = {
    earnings: { label: 'Earnings', cls: 'badge-bull'   },
    fda:      { label: 'FDA',      cls: 'badge-violet' },
    upgrade:  { label: 'Upgrade',  cls: 'badge-sky'    },
    contract: { label: 'Contract', cls: 'badge-sky'    },
    macro:    { label: 'Macro',    cls: 'badge-amber'  },
    social:   { label: 'Social',   cls: 'badge-amber'  },
    unknown:  { label: 'News',     cls: 'badge-amber'  },
  };

  if (!sym) {
    return (
      <div className="flex items-center justify-center h-full text-ink-3 text-xs p-4 text-center">
        Select a ticker to see catalyst news
      </div>
    );
  }

  return (
    <div className="p-3 overflow-y-auto">
      <div className="section-label mb-3">Catalyst Feed — {sym}</div>
      {safeNews.length === 0 ? (
        <div className="text-2xs text-ink-3 leading-relaxed">
          {!sym
            ? 'Select a ticker to see catalyst news'
            : ticker.isLoading
            ? 'Fetching news…'
            : 'No recent news found for this ticker. Verify catalyst manually on SEC EDGAR or Benzinga.'}
        </div>
      ) : (
        safeNews.map((item, i) => {
          const cat  = catConfig[item.catalystType ?? 'unknown'] ?? catConfig.unknown;
          const age  = (() => {
            if (!item.publishedAt) return '';
            const diff = Date.now() - new Date(item.publishedAt).getTime();
            const h    = Math.floor(diff / 3600000);
            const m    = Math.floor((diff % 3600000) / 60000);
            return h > 0 ? `${h}h ago` : `${m}m ago`;
          })();

          return (
            <div key={i} className="py-3 border-b border-border last:border-0">
              <div className="flex justify-between text-2xs text-ink-3 mb-1">
                <span>{item.source ?? ''}</span>
                <span>{age}</span>
              </div>
              <div className="text-xs text-ink-1 leading-relaxed mb-1.5">
                {item.headline ?? ''}
              </div>
              <div className="flex items-center gap-2">
                <span className={clsx('badge', cat.cls)}>{cat.label}</span>
                {item.sentiment && item.sentiment !== 'neutral' && (
                  <span className={clsx('text-2xs font-mono',
                    item.sentiment === 'bullish' ? 'text-bull' : 'text-bear')}>
                    {item.sentiment === 'bullish' ? '▲ bullish' : '▼ bearish'}
                  </span>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Right Panel Container ─────────────────────────────────────
// Journal + Analytics live in their own top-level tab now (see
// components/journal/JournalPanel.tsx and the "Journal & Analytics" main
// tab in App.tsx) — this sidebar only holds AI / News. The position sizer
// that used to live here was removed once the Auto-Trader tab's own risk
// limits (account balance, risk per trade) covered the same job for real.

export function RightPanel() {
  const { rightTab, setRightTab } = useUi();

  const tabs: { id: RightPanelTab; label: string; Icon: typeof Bot }[] = [
    { id: 'ai',   label: 'AI',    Icon: Bot        },
    { id: 'news', label: 'News',  Icon: Newspaper  },
  ];

  return (
    <aside className="w-full h-full bg-bg-1 lg:border-l border-border flex flex-col overflow-hidden">

      {/* Tab bar */}
      <div className="flex border-b border-border flex-shrink-0">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setRightTab(id)}
            className={clsx('tab-btn flex-1 flex items-center justify-center gap-1',
              rightTab === id && 'active')}
          >
            <Icon size={11} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {rightTab === 'ai'   && <AICoachPanel />}
        {rightTab === 'news' && <NewsPanel />}
      </div>
    </aside>
  );
}
